import { Alert, Center, Loader, Paper, Stack } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import {
  expenditureColumns,
  incomeColumns,
  lifeEventColumns,
  withMemberColumn,
} from 'src/cashflows/cashflowColumns';
import {
  ExpenditureSectionTotal,
  IncomeSectionTotal,
  LifeEventSectionTotal,
} from 'src/cashflows/components/cashflowCells';
import { expenditureName, incomeName, lifeEventName, withMemberName } from 'src/cashflows/leftOut';
import CashflowSection from 'src/cashflows/components/CashflowSection';
import CashflowTable from 'src/cashflows/components/CashflowTable';
import { Period } from 'src/cashflows/utils';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { CashflowModelsSection } from 'src/cashflows/modelling/CashflowModelsSection';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { FamilyMember } from './useFamilyMembers';
import {
  FamilyExpenditure,
  FamilyIncome,
  FamilyLifeEvent,
  useFamilyCashflows,
} from './useFamilyCashflows';

export function FamilyCashflowPanel({
  familyId,
  members,
}: {
  familyId: string;
  members: FamilyMember[];
}) {
  const currency = useCurrency();
  const { incomes, expenditures, lifeEvents, isLoading, hasErrors } = useFamilyCashflows(members);
  const { flagEnabledCashflowModelling, flagEnabledFamilyCashflowModelling } = useFlags();
  const showModels = flagEnabledCashflowModelling && flagEnabledFamilyCashflowModelling;
  const selectableMembers = members.map(member => ({
    clientId: member.clientId,
    name: formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL),
  }));
  const modelsSection = showModels ? (
    <CashflowModelsSection scope={{ kind: 'family', id: familyId }} members={selectableMembers} />
  ) : null;

  const [incomePeriod, setIncomePeriod] = useState<Period>('monthly');
  const [expenditurePeriod, setExpenditurePeriod] = useState<Period>('monthly');

  if (isLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {modelsSection}
      {hasErrors && (
        <Alert
          variant="light"
          color="yellow"
          icon={<IconAlertTriangle size={18} />}
          title="Some data could not be loaded"
        >
          Cashflow data for one or more family members failed to load, so the totals below may be
          incomplete.
        </Alert>
      )}

      <CashflowSection
        value="incomes"
        title="Income"
        count={incomes.length}
        period={incomePeriod}
        onPeriodChange={setIncomePeriod}
        showPeriodToggle
        totalNode={
          <IncomeSectionTotal
            incomes={incomes}
            period={incomePeriod}
            currency={currency}
            nameOf={withMemberName(incomeName)}
          />
        }
      >
        <CashflowTable<FamilyIncome>
          items={incomes}
          columns={withMemberColumn(incomeColumns<FamilyIncome>(incomePeriod, currency))}
        />
      </CashflowSection>

      <CashflowSection
        value="expenditures"
        title="Expenditure"
        count={expenditures.length}
        period={expenditurePeriod}
        onPeriodChange={setExpenditurePeriod}
        showPeriodToggle
        totalNode={
          <ExpenditureSectionTotal
            expenditures={expenditures}
            period={expenditurePeriod}
            currency={currency}
            nameOf={withMemberName(expenditureName)}
          />
        }
      >
        <CashflowTable<FamilyExpenditure>
          items={expenditures}
          columns={withMemberColumn(
            expenditureColumns<FamilyExpenditure>(expenditurePeriod, currency),
          )}
        />
      </CashflowSection>

      <CashflowSection
        value="lifeEvents"
        title="Future life events"
        count={lifeEvents.length}
        totalNode={
          <LifeEventSectionTotal
            lifeEvents={lifeEvents}
            currency={currency}
            nameOf={withMemberName(lifeEventName)}
          />
        }
      >
        <CashflowTable<FamilyLifeEvent>
          items={lifeEvents}
          columns={withMemberColumn(lifeEventColumns<FamilyLifeEvent>(currency))}
        />
      </CashflowSection>
    </Stack>
  );
}
